import type { Moment } from "moment";
import type { Client, GlobalAndSpaceRootLinks, RequestContext } from "../client";
import type { GetDeploymentFreezeDetailBffResponse, CreateDeploymentFreezeCommand, ProjectResource } from "../resources";
import type { DeploymentFreezeFilterQueryParameters, DeploymentFreezeOverviewBffResponse } from "../resources/deploymentFreezeOverviewBffResponse";
import type { CreateDeploymentFreezeResponse, GetDeploymentFreezeByIdResponse } from "../resources/deploymentFreezes/createDeploymentFreezeResponse";
import type { GetDeploymentFreezesResponse } from "../resources/deploymentFreezes/getDeploymentFreezesResponse";
import type { GetRecurringFreezePreviewBffRequest, GetRecurringFreezePreviewBffResponse } from "../resources/deploymentFreezes/getRecurringFreezePreviewResponse";
import type { GetTenantConnectedProjectBffResponse } from "../resources/deploymentFreezes/getTenantConnectedProjectBff";
import type { ModifyDeploymentFreezeCommand } from "../resources/deploymentFreezes/modifyDeploymentFreezeCommand";
import type { ModifyDeploymentFreezeResponse } from "../resources/deploymentFreezes/modifyDeploymentFreezeResponse";
import type { SearchAvailableEnvironmentsBffResponse } from "../resources/deploymentFreezes/searchAvailableEnvironmentsBffResponse";
import { requestFeatureTrial } from "../resources/requestFeatureTrial";
export class DeploymentFreezesRepository {
    protected client: Client;
    collectionLinkName: GlobalAndSpaceRootLinks = "DeploymentFreezes";
    constructor(client: Client, private readonly requestContext: RequestContext) {
        this.client = client;
    }
    async get(freezId: string): Promise<GetDeploymentFreezeByIdResponse> {
        return await this.client.get<GetDeploymentFreezeByIdResponse>(this.requestContext, `~/api/deploymentfreezes/${freezId}`);
    }
    async create(createDeploymentFreezeCommand: CreateDeploymentFreezeCommand): Promise<CreateDeploymentFreezeResponse> {
        return await this.client.post<CreateDeploymentFreezeResponse>(this.requestContext, "~/api/deploymentfreezes", createDeploymentFreezeCommand);
    }
    async modify(modifyDeploymentFreezeCommand: ModifyDeploymentFreezeCommand): Promise<ModifyDeploymentFreezeResponse> {
        return await this.client.put<ModifyDeploymentFreezeResponse>(this.requestContext, `~/api/deploymentfreezes/${modifyDeploymentFreezeCommand.Id}`, modifyDeploymentFreezeCommand);
    }
    async delete(id: string) {
        return this.client.delete(this.requestContext, `~/api/deploymentfreezes/${id}`);
    }
    async getDetailBff(deploymentFreezeId: string, projectId?: string): Promise<GetDeploymentFreezeDetailBffResponse> {
        const template = `~/bff/deploymentfreezes/${deploymentFreezeId}{?projectId}`;
        const response = await this.client.get<GetDeploymentFreezeDetailBffResponse>(this.requestContext, template, { projectId });
        return response;
    }
    async requestTrial() {
        return await requestFeatureTrial({ TypeOfFeature: "GlobalDeploymentFreeze" }, this.client, this.requestContext);
    }
    getOverviewBff(params?: DeploymentFreezeFilterQueryParameters): Promise<DeploymentFreezeOverviewBffResponse> {
        const template = "~/bff/deploymentfreezes/overview{?pageNumber,pageSize,filterByProjects,filterByTenants,filterByEnvironments,filterByStatus,filterByName}";
        return this.client.get<DeploymentFreezeOverviewBffResponse>(this.requestContext, template, { ...params });
    }
    list(project: ProjectResource, tenantIds?: string[]): Promise<GetDeploymentFreezesResponse> {
        const link = this.client.getLink(this.collectionLinkName);
        return this.client.get(this.requestContext, link, { skip: 0, take: 1337, projectIds: [project.Id], tenantIds, includeComplete: false });
    }
    listEffectiveDate(project: ProjectResource, tenantIds?: string[], effectiveDate?: Moment): Promise<GetDeploymentFreezesResponse> {
        const link = this.client.getLink(this.collectionLinkName);
        const effectiveDateString = effectiveDate?.toISOString();
        const params = { skip: 0, take: 1337, projectIds: [project.Id], tenantIds, includeComplete: false, effectiveDate: effectiveDateString };
        return this.client.get(this.requestContext, link, params);
    }
    activeByProject(project: ProjectResource): Promise<GetDeploymentFreezesResponse> {
        const link = this.client.getLink(this.collectionLinkName);
        return this.client.get(this.requestContext, link, { skip: 0, take: 1337, projectIds: [project.Id], includeComplete: false, status: "Active" });
    }
    activeByTenant(tenantIds: string[]): Promise<GetDeploymentFreezesResponse> {
        const link = this.client.getLink(this.collectionLinkName);
        return this.client.get(this.requestContext, link, { skip: 0, take: 1337, tenantIds, includeComplete: false, status: "Active" });
    }
    allActive(): Promise<GetDeploymentFreezesResponse> {
        const link = this.client.getLink(this.collectionLinkName);
        return this.client.get(this.requestContext, link, { skip: 0, take: 1337, includeComplete: false, status: "Active" });
    }
    searchAvailableEnvironments(tenantProjects: {
        tenantId: string;
        projectId: string;
    }[]): Promise<SearchAvailableEnvironmentsBffResponse> {
        return this.client.post<SearchAvailableEnvironmentsBffResponse>(this.requestContext, "~/bff/deploymentfreezes/environment-search", {
            tenantProjects: tenantProjects,
        });
    }
    getTenantConnectedProjects(tenantIds: string[], projectIds: string[]): Promise<GetTenantConnectedProjectBffResponse> {
        return this.client.post<GetTenantConnectedProjectBffResponse>(this.requestContext, "~/bff/deploymentfreezes/tenant-connected-projects", {
            tenantIds: tenantIds,
            projectIds: projectIds,
        });
    }
    getRecurringFreezePreview(request: GetRecurringFreezePreviewBffRequest): Promise<GetRecurringFreezePreviewBffResponse> {
        return this.client.post<GetRecurringFreezePreviewBffResponse>(this.requestContext, "~/bff/deploymentfreezes/recurring-freeze-preview", request);
    }
}
